import { useState } from 'react';
import { Pencil, Check, X, User, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ESTADOS_BRASILEIROS } from '@/lib/estados';

interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  sms_phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  birth_date: string | null;
  cluster: 'bom_pagador' | 'regular' | 'mau_pagador' | null;
  assigned_to: string | null;
  collection_rule_id: string | null;
  reminders_active: boolean | null;
  renegotiation_status: string | null;
}

interface ClienteCadastroTabProps {
  customer: Customer;
}

function maskTaxId(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

function formatPhone(value: string | null): string {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return value;
}

function EditableSection({ 
  title, 
  icon: Icon,
  children, 
  onSave,
  isEditing,
  setIsEditing
}: { 
  title: string; 
  icon: React.ElementType;
  children: React.ReactNode;
  onSave: () => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
}) {
  return (
    <div className="bg-card border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-bold text-[hsl(var(--basic-900))]">{title}</h3>
        </div>
        {isEditing ? (
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onSave}>
              <Check className="h-4 w-4 text-[hsl(var(--accent-500))]" />
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

export function ClienteCadastroTab({ customer }: ClienteCadastroTabProps) {
  const queryClient = useQueryClient();
  const [editingSection, setEditingSection] = useState<string | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: customer.name,
    tax_id: customer.tax_id,
    email: customer.email || '',
    phone: customer.phone || '',
    whatsapp: customer.whatsapp || customer.phone || '',
    sms_phone: customer.sms_phone || customer.phone || '',
    address: customer.address || '',
    city: customer.city || '',
    state: customer.state || '',
    postal_code: customer.postal_code || '',
    birth_date: customer.birth_date || '',
    assigned_to: customer.assigned_to || '',
    collection_rule_id: customer.collection_rule_id || '',
    reminders_active: customer.reminders_active ?? true,
    renegotiation_status: customer.renegotiation_status || 'eligible',
  });

  const { data: reguas = [] } = useQuery({
    queryKey: ['collection-rules'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_rules')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<typeof formData>) => {
      const { error } = await supabase
        .from('customers')
        .update(updates)
        .eq('id', customer.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
      setEditingSection(null);
    }
  });

  const clusterColors: Record<string, string> = {
    bom_pagador: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]',
    regular: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))]',
    mau_pagador: 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]'
  };

  const clusterLabels: Record<string, string> = {
    bom_pagador: 'Bom Pagador',
    regular: 'Regular',
    mau_pagador: 'Inadimplente'
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Dados do Cliente */}
      <EditableSection
        title="Dados do cliente"
        icon={User}
        isEditing={editingSection === 'dados'}
        setIsEditing={(v) => setEditingSection(v ? 'dados' : null)}
        onSave={() => updateMutation.mutate({
          name: formData.name,
          tax_id: formData.tax_id.replace(/\D/g, ''),
          birth_date: formData.birth_date || undefined,
        })}
      >
        {editingSection === 'dados' ? (
          <div className="space-y-3">
            <div>
              <Label>Nome / Razão Social</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})} 
              />
            </div>
            <div>
              <Label>CPF / CNPJ</Label>
              <Input 
                value={formData.tax_id} 
                onChange={(e) => setFormData({...formData, tax_id: e.target.value})} 
              />
            </div>
            <div>
              <Label>Data de Nascimento</Label>
              <Input 
                type="date"
                value={formData.birth_date} 
                onChange={(e) => setFormData({...formData, birth_date: e.target.value})} 
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">CPF/CNPJ</span>
                <p className="font-mono text-[hsl(var(--basic-900))]">{maskTaxId(customer.tax_id)}</p>
              </div>
              {customer.cluster && (
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${clusterColors[customer.cluster]}`}>
                  {clusterLabels[customer.cluster]}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Régua</span>
                <p className="text-[hsl(var(--basic-700))]">
                  {reguas.find(r => r.id === customer.collection_rule_id)?.name || 'Não atribuída'}
                </p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Lembretes</span>
                <p className={customer.reminders_active ? 'text-[hsl(var(--accent-600))]' : 'text-muted-foreground'}>
                  {customer.reminders_active ? 'ATIVO' : 'INATIVO'}
                </p>
              </div>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Renegociação</span>
              <p className="text-[hsl(var(--basic-700))] capitalize">
                {customer.renegotiation_status === 'eligible' ? 'Apto' : customer.renegotiation_status}
              </p>
            </div>
          </div>
        )}
      </EditableSection>

      {/* Régua e Configurações */}
      <EditableSection
        title="Configurações de cobrança"
        icon={User}
        isEditing={editingSection === 'config'}
        setIsEditing={(v) => setEditingSection(v ? 'config' : null)}
        onSave={() => updateMutation.mutate({
          collection_rule_id: formData.collection_rule_id || undefined,
          reminders_active: formData.reminders_active,
          renegotiation_status: formData.renegotiation_status,
        })}
      >
        {editingSection === 'config' ? (
          <div className="space-y-3">
            <div>
              <Label>Régua de Cobrança</Label>
              <Select 
                value={formData.collection_rule_id} 
                onValueChange={(v) => setFormData({...formData, collection_rule_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma régua" />
                </SelectTrigger>
                <SelectContent>
                  {reguas.map((regua) => (
                    <SelectItem key={regua.id} value={regua.id}>{regua.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={formData.reminders_active}
                onChange={(e) => setFormData({...formData, reminders_active: e.target.checked})}
                className="rounded"
              />
              <Label>Lembretes ativos</Label>
            </div>
            <div>
              <Label>Status Renegociação</Label>
              <Select 
                value={formData.renegotiation_status} 
                onValueChange={(v) => setFormData({...formData, renegotiation_status: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eligible">Apto</SelectItem>
                  <SelectItem value="in_negotiation">Em Negociação</SelectItem>
                  <SelectItem value="blocked">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Régua</span>
              <p className="font-medium text-[hsl(var(--basic-900))]">
                {reguas.find(r => r.id === customer.collection_rule_id)?.name || 'Não atribuída'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Lembretes</span>
                <p className={customer.reminders_active ? 'text-[hsl(var(--accent-600))] font-medium' : 'text-muted-foreground'}>
                  {customer.reminders_active ? 'ATIVO' : 'INATIVO'}
                </p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Renegociação</span>
                <p className="font-medium text-[hsl(var(--basic-700))] capitalize">
                  {customer.renegotiation_status === 'eligible' ? 'Apto' : 
                   customer.renegotiation_status === 'in_negotiation' ? 'Em Negociação' : 'Bloqueado'}
                </p>
              </div>
            </div>
          </div>
        )}
      </EditableSection>

      {/* Contato */}
      <EditableSection
        title="Contato"
        icon={Phone}
        isEditing={editingSection === 'contato'}
        setIsEditing={(v) => setEditingSection(v ? 'contato' : null)}
        onSave={() => updateMutation.mutate({
          email: formData.email || undefined,
          phone: formData.phone?.replace(/\D/g, '') || undefined,
          whatsapp: formData.whatsapp?.replace(/\D/g, '') || undefined,
          sms_phone: formData.sms_phone?.replace(/\D/g, '') || undefined,
        })}
      >
        {editingSection === 'contato' ? (
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})} 
              />
            </div>
            <div>
              <Label>WhatsApp</Label>
              <Input 
                value={formData.whatsapp} 
                onChange={(e) => setFormData({...formData, whatsapp: e.target.value})} 
              />
            </div>
            <div>
              <Label>SMS</Label>
              <Input 
                value={formData.sms_phone} 
                onChange={(e) => setFormData({...formData, sms_phone: e.target.value})} 
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Email</span>
              <p className="text-[hsl(var(--basic-700))] truncate">{customer.email || '-'}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">WhatsApp</span>
              <p className="text-[hsl(var(--basic-700))]">{formatPhone(customer.whatsapp || customer.phone)}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">SMS</span>
              <p className="text-[hsl(var(--basic-700))]">{formatPhone(customer.sms_phone || customer.phone)}</p>
            </div>
          </div>
        )}
      </EditableSection>

      {/* Endereço */}
      <EditableSection
        title="Endereço"
        icon={MapPin}
        isEditing={editingSection === 'endereco'}
        setIsEditing={(v) => setEditingSection(v ? 'endereco' : null)}
        onSave={() => updateMutation.mutate({
          postal_code: formData.postal_code?.replace(/\D/g, '') || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
        })}
      >
        {editingSection === 'endereco' ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>CEP</Label>
                <Input 
                  value={formData.postal_code} 
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})} 
                />
              </div>
              <div>
                <Label>Estado</Label>
                <Select 
                  value={formData.state} 
                  onValueChange={(v) => setFormData({...formData, state: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="UF" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_BRASILEIROS.map((uf) => (
                      <SelectItem key={uf.sigla} value={uf.sigla}>{uf.sigla}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Cidade</Label>
              <Input 
                value={formData.city} 
                onChange={(e) => setFormData({...formData, city: e.target.value})} 
              />
            </div>
            <div>
              <Label>Endereço</Label>
              <Input 
                value={formData.address} 
                onChange={(e) => setFormData({...formData, address: e.target.value})} 
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">CEP</span>
                <p className="text-[hsl(var(--basic-700))] font-mono">
                  {customer.postal_code ? customer.postal_code.replace(/(\d{5})(\d{3})/, '$1-$2') : '-'}
                </p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Estado</span>
                <p className="text-[hsl(var(--basic-700))]">{customer.state || '-'}</p>
              </div>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Cidade</span>
              <p className="text-[hsl(var(--basic-700))]">{customer.city || '-'}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Endereço</span>
              <p className="text-[hsl(var(--basic-700))]">{customer.address || '-'}</p>
            </div>
          </div>
        )}
      </EditableSection>
    </div>
  );
}
